import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MessageCircle, Plus, MoreVertical, Menu, LogOut, Settings } from 'lucide-react';
import { useAtom } from 'jotai';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { RainbowButton } from '../magicui/rainbow-button';
import { ShinyButton } from '../magicui/shiny-button';
import { cn } from '../../lib/utils';
import { spanTypography } from '../../styles/typography';
import { logoutAtom, currentUserAtom } from '../../store/auth';
import UploadModal from '../UploadModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface TopHeaderProps {
  pageTitle?: string;
  breadcrumb?: string;
  onChatOpen?: () => void;
  onMobileSidebarToggle?: () => void;
  isMobile?: boolean;
  className?: string;
}

const TopHeader: React.FC<TopHeaderProps> = ({
  pageTitle = "Talent Showcase",
  breadcrumb = "Dashboard / Talent Showcase",
  onChatOpen,
  onMobileSidebarToggle,
  isMobile = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const [, logout] = useAtom(logoutAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const [showSearchToast, setShowSearchToast] = React.useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const logoutButtonRef = React.useRef<HTMLDivElement>(null);

  // Use native DOM event to bypass React synthetic event system
  // Wait for dropdown to open before attaching event listener
  React.useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const handleLogoutClick = async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Close dropdown first
      setIsDropdownOpen(false);

      // Small delay to ensure dropdown closes cleanly
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        await logout();
      } catch (error) {
        console.error('Logout failed:', error);
      }
    };

    // Wait for portal to render
    const timer = setTimeout(() => {
      const logoutButton = logoutButtonRef.current;
      if (!logoutButton) return;

      logoutButton.addEventListener('click', handleLogoutClick, { capture: true });
    }, 50); // Wait 50ms for portal to render

    return () => {
      clearTimeout(timer);
      const logoutButton = logoutButtonRef.current;
      if (logoutButton) {
        logoutButton.removeEventListener('click', handleLogoutClick, { capture: true });
      }
    };
  }, [logout, isDropdownOpen]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setShowSearchToast(true);
      setTimeout(() => setShowSearchToast(false), 3000);
    }
  };
  return (
    <header className={`bg-white border-b border-gray-200 px-3 sm:px-6 py-2 ${className}`}>
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Left side - Mobile hamburger + TalRev branding */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mobile hamburger menu */}
          {isMobile && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onMobileSidebarToggle}
              className="p-2 hover:bg-gray-100"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          
          {/* TalRev branding */}
          <Link to="/" className="flex items-center gap-1 sm:gap-2 hover:opacity-80 transition-opacity">
            <img 
              src="/AWS_logo_RGB_1c_Gray850.svg" 
              alt="AWS Logo" 
              className="h-5 sm:h-6 w-auto"
            />
            <span className="text-gray-400 text-[var(--font-size-lg)] sm:text-[var(--font-size-xl)] font-light">|</span>
            <span className="talrev-logo text-[var(--font-size-lg)] sm:text-[var(--font-size-2xl)] font-[var(--font-weight-extrabold)] text-gray-900 tracking-wide">
              talrev.
            </span>
          </Link>
        </div>

        {/* Center - Search bar (hidden on mobile) */}
        {!isMobile && (
          <div className="flex-1 max-w-xs sm:max-w-md mx-2 sm:mx-8 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[var(--font-size-sm)]"
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            {/* Toast notification */}
            {showSearchToast && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-lg z-50 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-start gap-2">
                  <span className="text-lg">🚀</span>
                  <div className="flex-1">
                    <p className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-blue-900">
                      Search is coming soon!
                    </p>
                    <p className="text-[var(--font-size-xs)] text-blue-700 mt-0.5">
                      We're building powerful search across all talent profiles.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right side - Action buttons */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          {/* Chat button - hide text on mobile */}
          <ShinyButton
            onClick={onChatOpen}
            className="flex items-center justify-center gap-2 sm:gap-4 text-[var(--font-size-xs)] font-[var(--font-family-primary)] px-2 sm:px-4 h-8 rounded-xl bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-50 min-w-[40px] sm:min-w-[80px]"
          >
            <MessageCircle className="h-4 w-4 flex-shrink-0" />
            <span className={cn(spanTypography.buttonSpan.secondary, "whitespace-nowrap hidden sm:inline")}>Chat</span>
          </ShinyButton>

          {/* Upload card button - hide text on mobile */}
          <RainbowButton
            size="sm"
            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Plus className="h-4 w-4 text-white" />
            <span className={`${spanTypography.buttonSpan.secondary} text-white hidden sm:inline`}>Upload Card</span>
          </RainbowButton>

          {/* Action buttons group */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            {/* More options dropdown */}
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-1.5 sm:p-2">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setIsDropdownOpen(false);
                    navigate('/settings');
                  }}
                  className="cursor-pointer"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <div
                  ref={logoutButtonRef}
                  role="menuitem"
                  tabIndex={0}
                  className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-[var(--font-size-sm)] outline-none transition-colors hover:bg-red-50 focus:bg-red-50 text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                  Logout {currentUser && `(${currentUser})`}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </header>
  );
};

export default TopHeader;